import { ArrowLeft, Inbox, Maximize2, Settings, SquarePen, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { type ChatInbox, fetchInbox, useChatLive } from '../../lib/chat';
import { Alert } from '../ui';
import { ConversationList } from './ConversationList';

/** The inbox, kept current by the live connection. */
export function useInbox() {
  const [inbox, setInbox] = useState<ChatInbox | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => {
    fetchInbox()
      .then((next) => {
        setInbox(next);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);
  useEffect(load, [load]);
  useChatLive(true, load);
  return { inbox, failed, reload: load };
}

const iconButton = 'flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-field';

/**
 * Messages' list (M38): Chats and Requests, with a new message button. In the chat window
 * (`dock`) a chat opens in place; on the page, by link.
 */
export function InboxPanel({
  variant,
  tab,
  onTab,
  current,
  onOpen,
  linkTo,
  onNew,
  onClose,
  onBack,
}: {
  variant: 'dock' | 'page';
  tab: 'chats' | 'requests';
  onTab: (tab: 'chats' | 'requests') => void;
  current?: string | null;
  onOpen?: (id: string) => void;
  linkTo?: (id: string) => string;
  onNew: () => void;
  onClose?: () => void;
  /** Phone: back out of Messages. */
  onBack?: () => void;
}) {
  const { inbox, failed } = useInbox();
  const chats = inbox?.conversations.filter((c) =>
    tab === 'requests' ? c.status === 'request' : c.status === 'accepted',
  );
  const Heading = variant === 'page' ? 'h1' : 'h2';

  return (
    <section aria-labelledby="inbox-title" className="flex min-h-0 flex-1 flex-col bg-card">
      <header
        className={`flex min-h-16 shrink-0 items-center gap-0.5 pr-2 ${onBack ? 'pl-1.5 lg:pl-[18px]' : 'pl-[18px]'}`}
      >
        {onBack && (
          <button
            type="button"
            aria-label="Back"
            onClick={onBack}
            className={`${iconButton} mr-1 lg:hidden`}
          >
            <ArrowLeft size={20} strokeWidth={1.9} aria-hidden="true" />
          </button>
        )}
        <Heading id="inbox-title" className="flex-1 text-[22px] leading-7 font-bold">
          Messages
        </Heading>
        <button type="button" aria-label="New message" onClick={onNew} className={iconButton}>
          <SquarePen size={20} strokeWidth={1.9} aria-hidden="true" />
        </button>
        {variant === 'dock' ? (
          <Link to="/messages" aria-label="Open Messages as a page" className={iconButton}>
            <Maximize2 size={18} strokeWidth={1.9} aria-hidden="true" />
          </Link>
        ) : (
          <Link to="/account/chat" aria-label="Chat settings" className={iconButton}>
            <Settings size={20} strokeWidth={1.9} aria-hidden="true" />
          </Link>
        )}
        {onClose && (
          <button
            type="button"
            aria-label="Close messages"
            onClick={onClose}
            className={iconButton}
          >
            <X size={20} strokeWidth={1.9} aria-hidden="true" />
          </button>
        )}
      </header>
      <div
        role="tablist"
        aria-label="Messages"
        className="mx-4 mb-2 grid grid-cols-2 rounded-full bg-field p-1"
      >
        {(['chats', 'requests'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => onTab(value)}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-full text-sm font-semibold ${tab === value ? 'bg-card text-ink shadow-[0_1px_2px_rgba(27,21,47,0.1)]' : 'text-muted hover:text-ink'}`}
          >
            {value === 'chats' ? 'Chats' : 'Requests'}
            {value === 'requests' && inbox?.requests ? (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-ink">
                {inbox.requests}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {tab === 'requests' && (
          <p className="mx-3 mt-1 mb-1.5 text-[13px] leading-[18px] text-muted">
            Messages from people you haven’t chatted with. They don’t know you’ve seen them until
            you accept.
          </p>
        )}
        {failed && !inbox ? (
          <div className="p-3">
            <Alert>Could not load your messages. Try again in a moment.</Alert>
          </div>
        ) : !chats ? (
          <p role="status" className="p-4 text-sm text-muted">
            Loading…
          </p>
        ) : chats.length ? (
          <ConversationList chats={chats} current={current} onOpen={onOpen} linkTo={linkTo} />
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-field text-muted">
              <Inbox size={20} strokeWidth={1.9} aria-hidden="true" />
            </span>
            <p className="font-semibold">{tab === 'chats' ? 'No chats yet' : 'No requests'}</p>
            <p className="text-sm text-muted">
              {tab === 'chats'
                ? 'Message someone you’ve played with. Your chats show up here.'
                : 'When someone new messages you, it waits here.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
